import { useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { TenantProvider } from './contexts/TenantContext';
import TenantNotFound from './components/TenantNotFound';
import { useTenant } from './contexts/TenantContext';
import { AuthProvider } from './contexts/AuthContext';
import { GuestProvider } from './contexts/GuestContext';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import SignUpPage from '@pages/auth/SignUpPage';
import LoginPage from '@pages/auth/LoginPage';
import HomePage from './pages/auth/HomePage'
import CompetitionsLayout from '@/layouts/CompetitionsLayout';
import IndividualCompPage from '@pages/competitions/IndividualCompPage';
import { DataProvider } from './contexts/DataContext';
import MainPage from './pages/competitions/MainPage';
import ProfilePage from '@pages/user/ProfilePage';
import EventsPage from '@pages/community/EventsPage';
import HallofFamePage from '@pages/community/HallofFamePage';
import IndividualCompLayout from './layouts/IndividualCompLayout';
import { ModalProvider } from './contexts/ModalContext';
import { CompetitionProvider } from "@/contexts/CompetitionContext";
import { PartnerAdminProvider } from './contexts/PartnerAdminContext';
import PartnerAdminRoute from '@/components/auth/PartnerAdminRoute';
import PartnerAdminLayout from '@/layouts/PartnerAdminLayout';
import PartnerDashboardPage    from '@/pages/partner-admin/PartnerDashboardPage';
import PartnerCompetitionsPage from '@/pages/partner-admin/PartnerCompetitionsPage';
import PartnerSubmissionsPage  from '@/pages/partner-admin/PartnerSubmissionsPage';
import PartnerUsersPage        from '@/pages/partner-admin/PartnerUsersPage';
import PartnerTeamPage         from '@/pages/partner-admin/PartnerTeamPage';
import OAuthCallbackPage from '@/pages/auth/OAuthCallbackPage';
import PartnerSettingsPage from '@/pages/partner-admin/PartnerSettingsPage';


// Requires a real login token — no guests
function PrivateRoute({ children }) {
  const token = localStorage.getItem('accessToken');
  return token ? children : <Navigate to="/login" />;
}

// Allows logged-in users OR guests — blocks only unauthenticated non-guests
function GuestAllowedRoute({ children }) {
  const token = localStorage.getItem('accessToken');
  const isGuest = sessionStorage.getItem('zelospace_guest_mode') === 'true';
  if (token || isGuest) return children;
  return <Navigate to="/login" />;
}


function TenantGate({ children }) {
  const { loading, tenantMissing } = useTenant();
  if (loading) return null;
  // Wildcard DNS and certificate mean any *.zelospace.com host reaches the
  // app, so an unregistered subdomain would otherwise render the shell and
  // then fail silently. Only a 404 from the tenant lookup gets here - see
  // TenantContext.
  if (tenantMissing) return <TenantNotFound />;
  return children;
  }

function App() {
  return (
      <TenantProvider>
        <TenantGate>
          <AuthProvider>
            <GuestProvider>
              <DataProvider>
                <PartnerAdminProvider>
                  <ModalProvider>
                    <Router>
                      <Routes>
                        <Route path="/" element={<Navigate to="/login" replace />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/signup" element={<SignUpPage />} />
                        <Route path="/verify-email" element={<VerifyEmailPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/homepage" element={<HomePage />} />
                        <Route path="/auth/callback" element={<OAuthCallbackPage />} />

                        {/* Guests allowed: browse-only pages */}
                        <Route
                          path="/competitions"
                          element={
                            <GuestAllowedRoute>
                              <CompetitionsLayout>
                                <MainPage />
                              </CompetitionsLayout>
                            </GuestAllowedRoute>
                          }
                        />

                        <Route
                          path="/competition/:id"
                          element={
                            <GuestAllowedRoute>
                              <CompetitionProvider>
                                <IndividualCompLayout>
                                  <IndividualCompPage />
                                </IndividualCompLayout>
                              </CompetitionProvider>
                            </GuestAllowedRoute>
                          }
                        />

                        <Route
                          path="/events"
                          element={
                            <GuestAllowedRoute>
                              <CompetitionsLayout>
                                <EventsPage />
                              </CompetitionsLayout>
                            </GuestAllowedRoute>
                          }
                        />

                        <Route
                          path="/hof"
                          element={
                            <GuestAllowedRoute>
                              <CompetitionsLayout>
                                <HallofFamePage/>
                              </CompetitionsLayout>
                            </GuestAllowedRoute>
                          }
                        />

                        {/* Auth required: no guests */}
                        <Route
                          path="/profile"
                          element={
                            <PrivateRoute>
                              <CompetitionsLayout>
                                <ProfilePage />
                              </CompetitionsLayout>
                            </PrivateRoute>
                          }
                        />

                        {/* Partner Admin */}
                        <Route path="/partner-admin" element={<Navigate to="/partner-admin/dashboard" replace />} />

                        <Route path="/partner-admin/dashboard" element={
                          <PartnerAdminRoute>
                            <PartnerAdminLayout><PartnerDashboardPage /></PartnerAdminLayout>
                          </PartnerAdminRoute>
                        } />

                        <Route path="/partner-admin/competitions" element={
                          <PartnerAdminRoute>
                            <PartnerAdminLayout><PartnerCompetitionsPage /></PartnerAdminLayout>
                          </PartnerAdminRoute>
                        } />

                        <Route path="/partner-admin/submissions" element={
                          <PartnerAdminRoute>
                            <PartnerAdminLayout><PartnerSubmissionsPage /></PartnerAdminLayout>
                          </PartnerAdminRoute>
                        } />

                        <Route path="/partner-admin/users" element={
                          <PartnerAdminRoute>
                            <PartnerAdminLayout><PartnerUsersPage /></PartnerAdminLayout>
                          </PartnerAdminRoute>
                        } />

                        <Route path="/partner-admin/team" element={
                          <PartnerAdminRoute requiredRole="admin">
                            <PartnerAdminLayout><PartnerTeamPage /></PartnerAdminLayout>
                          </PartnerAdminRoute>
                        } />

                        <Route path="/partner-admin/settings" element={
                        <PartnerAdminRoute requiredRole="admin">
                          <PartnerAdminLayout><PartnerSettingsPage /></PartnerAdminLayout>
                        </PartnerAdminRoute>
                        } />

                      </Routes>
                    </Router>
                  </ModalProvider>
                </PartnerAdminProvider>
              </DataProvider>
            </GuestProvider>
          </AuthProvider>
        </TenantGate>
      </TenantProvider>
  );
}

export default App